import { Controller } from 'react-hook-form';
import {
  DateInput,
  PickerPosition,
  InputText,
  Select,
  Autocomplete,
  IconPaper,
  Dropzone,
  IconClose,
} from '@ui/src';
import { CreateCashCollectionFormTypes } from './types';

import cn from './styles.module.css';

const CreateCashCollectionForm = ({
  control,
  errors,
  currency,
  currencies,
  recipientAccounts,
  recipientAccountsIsLoading,
  onSearchRecipientAccounts,
  pspPartners,
  pspPartnersIsLoading,
  onSearchPspPartners,
  pspAccountDisabled,
  pspPartnerAccounts,
  pspPartnerAccountsIsLoading,
  onSearchPspPartnerAccounts,
  orderUploadId,
  receiptUploadId,
  setReceiptUploadId,
  setOrderUploadId,
  uploadOrderInputRef,
  uploadReceiptInputRef,
  fileUploadInProgress,
  onUpload,
  uploadedOrderFilesError,
  uploadedReceiptFilesError,
  selectedUploadType,
  orderUploadIsRequired,
  receiptUploadIsRequired,
  setValue,
  accountsList,
  clearErrors,
  uploadedComplianceDocumentErrors,
  uploadedReceiptDocumentsErrors,
  pspPartnerAccountsNextPageIsLoading,
  pspPartnerAccountsPagination,
  pspPartnerAccountsOnScroll,
}: CreateCashCollectionFormTypes) => (
  <div className={cn.paymentOrderForm}>
    <div className={cn.formGroup}>
      <Controller
        name="orderCreatedAt"
        control={control}
        render={({ field: { value, onChange } }) => (
          <DateInput
            title="Date created"
            value={value}
            onChange={(e) => onChange(e.toISOString())}
            placeholder="Enter date created"
            pickerPisition={PickerPosition.RIGHT}
            className={`${cn.transactionDateInput} ${
              errors?.orderCreatedAt?.message
                ? cn.transactionDateInputError
                : ''
            }`}
            onReset={() => onChange(undefined)}
            error={errors?.orderCreatedAt?.message}
            fullWidth
            withoutCustomSelector
          />
        )}
      />
    </div>
    <div className={`${cn.formGroup} ${cn.flex}`}>
      <div className={cn.flexItem}>
        <Controller
          name="sum"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <InputText
              name={name}
              value={value}
              title="Payment amount"
              placeholder="Enter amount"
              onChange={onChange}
              error={errors?.sum?.message}
              currency={currency}
              amountInput
            />
          )}
        />
      </div>
      <div className={cn.flexItem}>
        <Controller
          name="currencyId"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <Select
              name={name}
              value={value}
              list={currencies || []}
              onChange={onChange}
              error={errors?.currencyId?.message}
              title="Payment currency"
              placeholder="Select currency"
              fullWidth
            />
          )}
        />
      </div>
    </div>
    <div className={cn.formGroup}>
      <Controller
        name="recepientAccountId"
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <Autocomplete
            title="Beneficiary"
            list={recipientAccounts}
            isLoading={recipientAccountsIsLoading}
            value={value}
            placeholder="Select beneficiary"
            name={name}
            onChange={(e) => {
              onChange(e);

              const currentRecipient = accountsList?.find(
                (item) => item.id === e.target.value,
              );

              if (currentRecipient) {
                setValue('organizationId', currentRecipient.organizationId);
                clearErrors(['organizationId']);
              }
            }}
            onSearch={(searchText) => onSearchRecipientAccounts(searchText)}
            error={
              errors?.recepientAccountId?.message ||
              errors?.organizationId?.message
            }
            withClearButton
            fullWidth
          />
        )}
      />
    </div>
    <div className={cn.formGroup}>
      <Controller
        name="pspPartnerId"
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <Autocomplete
            title="Partner bank"
            list={pspPartners}
            isLoading={pspPartnersIsLoading}
            value={value}
            placeholder="Select Partner bank"
            name={name}
            onChange={onChange}
            onSearch={(searchText) => onSearchPspPartners(searchText)}
            error={errors?.pspPartnerId?.message}
            withClearButton
            fullWidth
          />
        )}
      />
    </div>
    <div className={cn.formGroup}>
      <Controller
        name="pspPartnerAccountId"
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <Autocomplete
            title="Partner Bank Account"
            list={pspPartnerAccounts}
            isLoading={pspPartnerAccountsIsLoading}
            value={value}
            placeholder="Select Partner Bank Account"
            name={name}
            onChange={onChange}
            onSearch={(searchText) => onSearchPspPartnerAccounts(searchText)}
            error={errors?.pspPartnerAccountId?.message}
            disabled={pspAccountDisabled}
            hasNextPage={pspPartnerAccountsPagination?.hasNextPage}
            onInfinateScroll={
              pspPartnerAccountsOnScroll
                ? () => pspPartnerAccountsOnScroll()
                : undefined
            }
            withInfiniteScroll={Boolean(pspPartnerAccountsOnScroll)}
            nextPageIsLoading={pspPartnerAccountsNextPageIsLoading}
            withClearButton
            fullWidth
          />
        )}
      />
    </div>
    <div className={cn.formGroup}>
      <Controller
        name="orderNumber"
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <InputText
            name={name}
            value={value}
            title="Cash bag number"
            placeholder="Enter Cash bag number"
            onChange={onChange}
            error={errors?.orderNumber?.message}
          />
        )}
      />
    </div>
    <div className={cn.uploadTitle}>Add Compliance batch</div>
    <Dropzone
      inputRef={uploadOrderInputRef}
      onUpload={(data: FileList) => onUpload(data, 'order')}
      fileUploadInProgress={Boolean(
        fileUploadInProgress && selectedUploadType === 'order',
      )}
      dropzoneText="Drag and drop сompliance batch file here or select file"
      dropzoneClassName={`${cn.dropzoneWrapper} ${cn.createCashCollectionDropzoneWrapper}`}
      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    />
    {orderUploadId && (
      <div className={cn.uploadedFile}>
        <IconPaper />
        Compliance batch
        <button
          type="button"
          className={cn.deleteUploadedFile}
          onClick={() => setOrderUploadId('')}
          aria-label="Close"
        >
          <IconClose width={16} height={16} />
        </button>
      </div>
    )}
    {uploadedComplianceDocumentErrors &&
      uploadedComplianceDocumentErrors?.length > 0 && (
        <ul className={cn.uploadDocumentsErrors}>
          {uploadedComplianceDocumentErrors?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${item.name}-${index}`}>
              - The document <strong>{item.name}</strong> was not uploaded.
              <br />
              <span>
                <strong>Reason:</strong> {item.error}
              </span>
            </li>
          ))}
        </ul>
      )}
    {orderUploadIsRequired && (
      <div className={cn.fileRequiredError}>Compliance batch is required</div>
    )}
    {uploadedOrderFilesError && uploadedOrderFilesError.length > 0 && (
      <ul className={cn.uploadDocumentErrors}>
        {uploadedOrderFilesError.map((item) => (
          <li key={item}>{`- ${item}`}</li>
        ))}
      </ul>
    )}
    <div className={`${cn.uploadTitle} ${cn.marginTop}`}>
      Add Cash collection receipt
    </div>
    <Dropzone
      inputRef={uploadReceiptInputRef}
      onUpload={(data: FileList) => onUpload(data, 'receipt')}
      fileUploadInProgress={Boolean(
        fileUploadInProgress && selectedUploadType === 'receipt',
      )}
      dropzoneText="Drag and drop receipt file here or select file"
      dropzoneClassName={`${cn.dropzoneWrapper} ${cn.createCashCollectionDropzoneWrapper}`}
    />
    {receiptUploadId && (
      <div className={cn.uploadedFile}>
        <IconPaper />
        Cash collection receipt
        <button
          type="button"
          className={cn.deleteUploadedFile}
          onClick={() => setReceiptUploadId('')}
          aria-label="Close"
        >
          <IconClose width={16} height={16} />
        </button>
      </div>
    )}
    {uploadedReceiptDocumentsErrors &&
      uploadedReceiptDocumentsErrors?.length > 0 && (
        <ul className={cn.uploadDocumentsErrors}>
          {uploadedReceiptDocumentsErrors?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${item.name}-${index}`}>
              - The document <strong>{item.name}</strong> was not uploaded.
              <br />
              <span>
                <strong>Reason:</strong> {item.error}
              </span>
            </li>
          ))}
        </ul>
      )}
    {receiptUploadIsRequired && (
      <div className={cn.fileRequiredError}>
        Cash collection receipt is required
      </div>
    )}
    {uploadedReceiptFilesError && uploadedReceiptFilesError.length > 0 && (
      <ul className={cn.uploadDocumentErrors}>
        {uploadedReceiptFilesError.map((item) => (
          <li key={item}>{`- ${item}`}</li>
        ))}
      </ul>
    )}
  </div>
);

export default CreateCashCollectionForm;
